import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { observable, action, autorun, toJS } from 'mobx'
import { autobind } from 'core-decorators'
import { inject, observer } from 'mobx-react'
import { withRouter } from 'react-router'

import { debounce } from 'lodash'

import Map from '../components/Map'
import Footer from '../../base/components/Footer'
import Filter from '../components/Filter'
import FilterResults from '../components/FilterResults'
import Info from '../components/Info'

import getPath from '../../../shared/utils/getPath'

const MAP_FILTER_COLLECTION = 'mapFilterCollection'

const DEFAULT_ZOOM = 8

const DEFAULT_CENTER = {
  lat: 48.853455, //50.0150808,
  lng: 10.590879, //10.8883743
}

import { USER_ICON, MUSEUM_ICON } from '../icons'

@withRouter
@inject('program', 'museum')
@observer
export default class MapContainer extends Component {
  /* eslint-disable react/sort-comp */
  @observable filter = {
    _programs: true,
    school: [],
    groupSize: undefined,
    grade: [1, 13],
    subject: [],
    region: [],
    selected: 'schools',
  };
  /* eslint-enable react/sort-comp */

  static propTypes = {
    location: PropTypes.object.isRequired,
    museum: PropTypes.object.isRequired,
    program: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props)

    props.museum.addCollection(MAP_FILTER_COLLECTION)
    autorun(() => {
      props.museum[MAP_FILTER_COLLECTION].length
      this.updateMarkers()
    })

    const mapper = type => (itm, museum) => ({ ...itm, type: itm.type || type, museum })
    const progMapper = mapper('program')
    const tourMapper = mapper('guidedTour')

    const debouncedUpdate = debounce((filter) => {
      this.props.museum
        .where(filter, { reset: true })
        .then(({ models }) => {
          this.props.museum[MAP_FILTER_COLLECTION].splice(
            0,
            this.props.museum[MAP_FILTER_COLLECTION].length,
            ...models.reduce((memo, model) => {
              return [
                ...memo,
                ...(toJS(model.programs).map(i => progMapper(i, model))),
                ...(toJS(model.guidedTours).map(i => tourMapper(i, model)))
              ]
            }, [])
          )
        })
    }, 500)

    let cachedFilterString = JSON.stringify(this.filter)
    let didFilter = false
    autorun(() => {
      const filterString = JSON.stringify(this.filter)
      if (cachedFilterString !== filterString) {
        cachedFilterString = filterString
        didFilter = true
        debouncedUpdate(toJS(this.filter))
      }
    })
    // initial filtering
    if (!didFilter) {
      debouncedUpdate(toJS(this.filter))
    }
  }

  hasFilter() {
    return (
      this.filter.school.length
      || this.filter.grade.length
      || this.filter.subject.length
      || this.filter.region.length
      || this.filter.groupSize
    )
  }

  getCurrentOffers() {
    if (this.hasFilter()) {
      return this.props.museum[MAP_FILTER_COLLECTION]
    }
    // create a fake list
    return this.props.museum.list.reduce((memo, museum) => {
      return [
        ...memo,
        ...[...toJS(museum.programs), ...toJS(museum.guidedTours)]
      ]
    })
  }

  @action
  updateMarkers() {
    const animation = window.google ? window.google.maps.Animation.DROP : null
    this.markers = this.getCurrentOffers()
      .reduce((memo, itm) => {
        if (memo.indexOf(itm.museum) === -1) {
          memo.push(itm.museum)
        }
        return memo
      }, [])
      .map(museum => ({
        type: 'museum',
        id: museum.id,
        position: {
          lat: museum.address.latitude * 1,
          lng: museum.address.longitude * 1
        },
        animation,
        icon: MUSEUM_ICON,
        showInfo: false,
        renderInfoContent: (marker, onClose) => {
          return <Info onClose={onClose} marker={marker} museum={museum} />
        }
      }))
  }

  componentDidMount() {
    if (this.props.location.search) {
      this.setFilter(this.props.location.query)

      // wether or not the user explicitly chose a filter (Schularten, Fächer, Landkreise)
      // only do this when the component is mounted!
      this.isFilterOpen = 'filter' in this.props.location.query
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setFilter(nextProps.location.query)
    }
  }

  @action
  setFilter(query) {
    const arrayFields = ['school', 'subject', 'region']
    const queryObj = Object.keys(query).reduce((memo, key) => {
      if (key === 'filter') {
        memo.selected = query[key]
      }
      else {
        try {
          memo[key] = JSON.parse(query[key])
          /* eslint-disable max-depth */
          if (arrayFields.indexOf(key) !== -1) {
            memo[key] = Array.isArray(memo[key]) ? memo[key] : [memo[key]]
          }
          if (key === 'groupSize') {
            memo[key] = parseInt(query[key].replace(/"/g, ''), 10)
            if (Number.isNaN(memo[key])) {
              memo[key] = undefined
            }
          }
          /* eslint-enable max-depth */
        }
        catch (ex) {
          /* ignore */
        }
      }
      return memo
    }, {})

    this.filter = {
      ...this.filter,
      ...queryObj
    }
  }

  @action
  setUserPosition(position) {
    this.setCenter(position)
    if (position) {
      this.setUserMarker(position)
      window.localStorage.userPosition = position
    }
  }

  @action
  setUserMarker(/* position */) {
    // this.userPosition = {
    //   lat: position.latitude,
    //   lng: position.longitude
    // }
  }

  @action
  setCenter(position) {
    let center = null

    if (!position) {
      return
    }

    if (position.latitude && position.longitude) {
      center = {
        lat: position.latitude,
        lng: position.longitude
      }
    }
    else if (position instanceof window.google.maps.LatLng) {
      center = {
        lat: position.latitude,
        lng: position.longitude
      }
    }

    if (center) {
      this.center = center
    }
  }

  @observable isFilterOpen = false;

  @observable markers = [];

  @observable mapOpts = null;

  @observable userPosition = DEFAULT_CENTER;

  @autobind
  @action
  handleMapLoaded() {
    const positionParser = (position) => {
      let coords
      if (position) {
        coords = position.coords
      }
      this.setUserPosition(coords)

      this.mapOpts = {
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,

        options: {
          zoomControl: true,
          zoomControlOptions: {
            style: window.google.maps.ZoomControlStyle.SMALL,
            position: window.google.maps.ControlPosition.RIGHT_BOTTOM
          },

          scaleControl: true,
          scaleControlOptions: {
            position: window.google.maps.ControlPosition.LEFT_BOTTOM
          },

          panControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          overviewMapControl: false
        }
      }
    }

    const errorHandler = () => positionParser()

    // XXX
    if (/*!platform.isMobile &&*/ 'geolocation' in window.navigator) {
      window.navigator.geolocation.getCurrentPosition(
        positionParser,
        errorHandler,
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      )
    }
    else {
      errorHandler()
    }
  }

  @autobind
  @action
  handleInfoOpen(targetMarker) {
    this.markers = this.markers.map((marker) => {
      if (marker.id === targetMarker.id) {
        return {
          ...marker,
          showInfo: true
        }
      }
      return marker
    })
  }

  @autobind
  @action
  handleInfoClose(targetMarker) {
    this.markers = this.markers.map((marker) => {
      if (marker.id === targetMarker.id) {
        return {
          ...marker,
          showInfo: false
        }
      }
      return marker
    })
  }

  @autobind
  @action
  handleToggleFilters() {
    this.isFilterOpen = !this.isFilterOpen
  }

  @autobind
  @action
  handleFilterChange({ target: { value } }) {
    const queryString = `?${Object.keys(value)
      .map(key => `${key}=${JSON.stringify(value[key])}`)
      .join('&')}`
    this.props.router.push(getPath('frontend', 'map', queryString))
  }

  @autobind
  handleRemoveFilter({ target: { value } }) {
    const filter = { ...this.filter }

    filter[value] = []

    const queryString = `?${Object.keys(filter)
      .map(key => `${key}=${JSON.stringify(filter[key])}`)
      .join('&')}`
    this.props.router.push(getPath('frontend', 'map', queryString))
  }

  render() {
    const markers = this.mapOpts
      ? [
        {
          type: 'user',
          id: 'user',
          position: this.userPosition,
          animation: window.google.maps.Animation.DROP,
          icon: USER_ICON
        },
        ...toJS(this.markers)
      ]
      : []

    const mapOpts = {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      ...this.mapOpts
    }

    return (
      <div className="container-fluid vertical">
        <div className="row no-margin horizontal">
          <div className="map-container-filters col-xs-12 col-sm-5 col-md-4 col-lg-3">
            <Filter
              open={this.isFilterOpen}
              onFilterClosed={this.handleToggleFilters}
              onFilterChanged={this.handleFilterChange}
              filter={this.filter}
              loading={this.props.museum.loading}
            />
          </div>

          <div className="map-container-results col-xs-12 col-sm-7 col-md-8 col-lg-9 no-padding">
            <FilterResults
              filter={this.filter}
              onFilterRemoved={this.handleRemoveFilter}
              onToggleFilters={this.handleToggleFilters}
              loading={this.props.museum.loading}
              collection={this.getCurrentOffers()}
            />

            <div className="hidden-xs" style={{ height: '100%' }}>
              <Map
                googleMapURL="//maps.googleapis.com/maps/api/js?key=AIzaSyDlmDlSiBiQXKCPyMWccIYxjkN5HJZvbTQ"
                loadingElement={
                  <div style={{ height: '100%' }}>Karte wird geladen</div>
                }
                containerElement={<div style={{ height: '100%' }} />}
                mapElement={<div style={{ height: '100%' }} />}
                onMapLoad={this.handleMapLoaded}
                onMapClick={() => { }}
                markers={markers}
                onMarkerClick={this.handleInfoOpen}
                onMarkerClose={this.handleInfoClose}
                mapOpts={mapOpts}
              />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/frontend/map/containers/MapContainer.js